import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import { eventsApi } from '../../api/eventsApi';
import { transcriptApi } from '../../api/transcriptApi';
import { CalendarDaysIcon, PlusCircleIcon, MagnifyingGlassIcon, TagIcon, PencilIcon, ClockIcon, DocumentChartBarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import EditEventModal from '../../components/events/EditEventModal';
import EventReportModal from '../../components/events/EventReportModal';
import EventReportDisplay from '../../components/events/EventReportDisplay';
import { formatDuration } from '../../utils/formatDuration';

export default function EventDetailPage() {
  const { id } = useParams();
  const { user, loading, error, getAuth } = useAuth();
  const [event, setEvent] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('date_desc');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportKey, setReportKey] = useState(0); // Key to force report reload
  const [reportStatus, setReportStatus] = useState(null);
  const [reportElapsedTime, setReportElapsedTime] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const statusOptions = useMemo(() => ([
    { value: 'all', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FAILED', label: 'Failed' },
  ]), []);

  const loadEventData = async () => {
    try {
      // Fetch event details from our backend
      const eventData = await eventsApi.getById(id);
      setEvent(eventData);
    } catch (e) {
      console.error('Failed to load event:', e);
    }
  };

  const loadSessions = async () => {
    try {
      // Fetch sessions for this event
      const res = await transcriptApi.getSessionsByEvent(id);
      // API returns { sessions: [...], total, skip, limit }
      const list = Array.isArray(res?.sessions) ? res.sessions : (Array.isArray(res?.results) ? res.results : (Array.isArray(res) ? res : []));
      setSessions(list);
    } catch (e) {
      console.error('Failed to load sessions:', e);
      setSessions([]);
    }
  };

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadingAll(true);
      await Promise.all([loadEventData(), loadSessions()]);
      setLoadingAll(false);
    };
    load();
  }, [id, user]);

  const handleSaveEvent = async (updateData) => {
    await eventsApi.update(id, updateData);
    await loadEventData(); // Reload event data after update
  };

  // Check report status
  const checkReportStatus = useCallback(async () => {
    if (!id) return;
    try {
      const status = await eventsApi.getReportStatus(id);
      setReportStatus(status);
      return status;
    } catch (err) {
      console.error('Error checking report status:', err);
      return null;
    }
  }, [id]);

  // Start polling for report status
  const startPolling = useCallback(() => {
    setIsPolling(true);
    
    const poll = async () => {
      const status = await checkReportStatus();
      if (status && (status.status === 'COMPLETED' || status.status === 'FAILED')) {
        setIsPolling(false);
        // Refresh report display when completed
        if (status.status === 'COMPLETED') {
          setReportKey(prev => prev + 1);
        }
        return;
      }
      // Continue polling
      setTimeout(poll, 3000);
    };
    
    poll();
  }, [checkReportStatus]);

  // Handle report generation started
  const handleReportGenerationStarted = useCallback((result) => {
    if (result === 'started') {
      // Start polling for status updates
      startPolling();
    }
  }, [startPolling]);

  // Elapsed time timer for report generation
  useEffect(() => {
    if (!reportStatus || (reportStatus.status !== 'PROCESSING' && reportStatus.status !== 'PENDING')) {
      setReportElapsedTime(null);
      return;
    }

    const startTime = reportStatus.queued_at ? new Date(reportStatus.queued_at) : 
                      reportStatus.processing_started_at ? new Date(reportStatus.processing_started_at) : 
                      new Date();

    const updateElapsed = () => {
      const now = new Date();
      const seconds = Math.floor((now - startTime) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes === 0) {
        setReportElapsedTime(`${remainingSeconds}sec`);
      } else if (remainingSeconds === 0) {
        setReportElapsedTime(`${minutes}min`);
      } else {
        setReportElapsedTime(`${minutes}min ${remainingSeconds}sec`);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [reportStatus]);

  // Check initial report status on load
  useEffect(() => {
    if (user && id) {
      checkReportStatus().then(status => {
        if (status && (status.status === 'PROCESSING' || status.status === 'PENDING')) {
          startPolling();
        }
      });
    }
  }, [user, id, checkReportStatus, startPolling]);

  if (!user) {
    return (
      <LoginForm 
        onSSOLogin={getAuth}
        ssoLoading={loading}
        ssoError={error}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2 gap-3">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <CalendarDaysIcon className="h-7 w-7 text-blue-600 mr-2" />
                  {event?.title || 'Event'}
                </h1>
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-blue-600 transition-colors"
                  title="Edit event"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              </div>
              {event?.description && (
                <p className="text-gray-600 mb-2">{event.description}</p>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                {event?.status && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    event.status === 'UPCOMING' ? 'bg-blue-100 text-blue-800' :
                    event.status === 'ONGOING' ? 'bg-green-100 text-green-800' :
                    event.status === 'COMPLETED' ? 'bg-purple-100 text-purple-800' :
                    event.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {event.status.replace('_', ' ')}
                  </span>
                )}
                {event?.is_active && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                    Active
                  </span>
                )}
                {event?.session_count !== undefined && (
                  <span className="text-sm text-gray-600">
                    {event.session_count} session{event.session_count !== 1 ? 's' : ''}
                  </span>
                )}
                {event?.tags && event.tags.length > 0 && (
                  <div className="flex items-center gap-1">
                    <TagIcon className="h-4 w-4 text-gray-400" />
                    {event.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="bg-gray-100 px-2 py-0.5 rounded text-xs text-gray-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Report Generation Status or Button */}
              {isPolling || (reportStatus && (reportStatus.status === 'PROCESSING' || reportStatus.status === 'PENDING')) ? (
                <div className="inline-flex items-center px-4 py-2.5 rounded-lg bg-purple-100 text-purple-800 border border-purple-200">
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  <div className="flex flex-col">
                    <span className="font-medium text-sm">
                      {reportStatus?.current_step || 'Generating Report...'}
                    </span>
                    <span className="text-xs text-purple-600">
                      {reportElapsedTime && `${reportElapsedTime}`}
                      {reportStatus?.progress > 0 && ` • ${reportStatus.progress}%`}
                    </span>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsReportModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  <DocumentChartBarIcon className="h-5 w-5 mr-2" />
                  Generate Report
                </button>
              )}
              <Link
                to="/upload"
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <PlusCircleIcon className="h-5 w-5 mr-2" />
                Add Session
              </Link>
            </div>
          </div>
          {/* Filters for sessions within event */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search sessions</label>
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Title or description..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort</label>
              <select value={sort} onChange={e => setSort(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="date_desc">Newest</option>
                <option value="date_asc">Oldest</option>
                <option value="title_asc">Title A-Z</option>
                <option value="title_desc">Title Z-A</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sessions</h2>

        {loadingAll && (
          <div className="bg-white border rounded-lg p-6 text-center">Loading...</div>
        )}

        {!loadingAll && sessions.length === 0 && (
          <div className="bg-white border rounded-lg p-6 text-center text-gray-600">No sessions for this event.</div>
        )}
        <div className="space-y-3">
          {sessions
            .filter(s => {
              const term = search.trim().toLowerCase();
              if (!term) return true;
              return (s.title || '').toLowerCase().includes(term) || (s.description || '').toLowerCase().includes(term);
            })
            .filter(s => status === 'all' ? true : (s.status === status))
            .sort((a,b) => {
              if (sort === 'date_desc') return new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0);
              if (sort === 'date_asc') return new Date(a.createdAt || a.created_at || 0) - new Date(b.createdAt || b.created_at || 0);
              if (sort === 'title_asc') return (a.title||'').localeCompare(b.title||'');
              if (sort === 'title_desc') return (b.title||'').localeCompare(a.title||'');
              return 0;
            })
            .map((s) => {
              const statusClass = s.status === 'COMPLETED' ? 'bg-green-50 border-green-200 text-green-700' : s.status === 'FAILED' ? 'bg-red-50 border-red-200 text-red-700' : s.status === 'IN_PROGRESS' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-gray-50 border-gray-200 text-gray-700';
              return (
                <div key={s.id || s.session_id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Link to={`/sessions/${s.id || s.session_id}`} className="text-lg font-semibold text-gray-900 hover:underline truncate">
                          {s.title || 'Untitled session'}
                        </Link>
                        <span className={`px-2 py-0.5 rounded-full border text-xs ${statusClass}`}>{s.status || 'N/A'}</span>
                        {(s.transcriptModel || s.transcript_model) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">{s.transcriptModel || s.transcript_model}</span>
                        )}
                      </div>
                      {s.description && (
                        <div className="text-sm text-gray-600 mt-1 line-clamp-2">{s.description}</div>
                      )}
                      {s.status === 'FAILED' && (s.errorMessage || s.error_message) && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                          <span className="font-medium">Error:</span> {s.errorMessage || s.error_message}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 text-xs text-gray-600">
                        <div>
                          <div className="text-gray-500">File</div>
                          <div className="truncate">{s.originalFilename || s.original_filename || s.filePath || s.file_path || s.fileUrl || s.file_url || '—'}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Processing</div>
                          <div>Started: {(s.processingStartedAt || s.processing_started_at) ? new Date(s.processingStartedAt || s.processing_started_at).toLocaleString() : '—'}</div>
                          <div>Completed: {(s.processingCompletedAt || s.processing_completed_at) ? new Date(s.processingCompletedAt || s.processing_completed_at).toLocaleString() : '—'}</div>
                          {(s.durationSeconds != null || s.duration_seconds != null) && (
                            <div className="flex items-center gap-1">
                              <ClockIcon className="h-3 w-3 text-gray-400" />
                              <span>Duration: {formatDuration(s.durationSeconds || s.duration_seconds)}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-gray-500">Meta</div>
                          <div>Created: {(s.createdAt || s.created_at) ? new Date(s.createdAt || s.created_at).toLocaleString() : '—'}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                      {(s.reportUrl || s.report_url) && (
                        <a href={s.reportUrl || s.report_url} target="_blank" rel="noreferrer" className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Open Report</a>
                      )}
                      <Link to={`/sessions/${s.id || s.session_id || s.sessionId}`} className="px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-center">Details</Link>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Event Report Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 border-t border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <DocumentChartBarIcon className="h-6 w-6 text-purple-600" />
            Event Report
          </h2>
          <button
            onClick={() => setIsReportModalOpen(true)}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Generate New Report
          </button>
        </div>
        
        {id && <EventReportDisplay key={reportKey} eventId={id} eventUrl={event?.url} onPublished={loadEventData} />}
      </div>

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEvent}
        event={event}
      />

      {/* Event Report Modal */}
      <EventReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        event={event}
        onReportGenerated={handleReportGenerationStarted}
      />
    </div>
  );
}


